// ============================================================================
// deck_persistence.ts
// Mémoire du deck PAR CHAÎNE : quel emplacement porte quelle source.
//
// Une entrée par emplacement. Elle décrit une INTENTION (« cet emplacement est
// celui de telle source »), pas l'état courant : une source absente laisse sa
// place réservée, vide, jusqu'à son retour.
//
//   • remote / other / layout : retrouvés par identifiant (stable d'un poste à
//     l'autre).
//   • device (webcam locale)  : l'identifiant est un deviceId navigateur, propre
//     au poste. On garde donc DEUX informations : les identifiants déjà portés
//     par cet emplacement (retour sur le poste d'origine = retrouvailles exactes)
//     ET le rang de la webcam parmi les webcams locales du deck (autre poste : la
//     n-ième webcam locale reprend l'emplacement de la n-ième).
// ============================================================================

import type { VideoSource, LayoutSlot } from '../../contexts/MediaStreamContext';
// Pas d'import de types_deck : il lit SLOT_COUNT de MediaStreamContext dès son
// évaluation, et MediaStreamContext importe ce fichier — cycle = page blanche.
// Valeur à garder identique à KEYMAP_STORAGE_KEY (types_deck.ts).
const KEYMAP_STORAGE_KEY = 'mediartc_deck_keymap';

export type DeckEntryKind = 'device' | 'remote' | 'other' | 'layout';

export interface DeckEntry {
  kind       : DeckEntryKind;
  /** Identifiants connus de cet emplacement, le plus récent en tête. */
  ids        : string[];
  /** Webcams locales seulement : rang parmi les webcams locales, par emplacement. */
  localRank? : number;
}

export type DeckEntries = Array<DeckEntry | null>;

const MAX_IDS = 6;
const DECK_BASE_KEY = 'mediartc_deck_slots_config';

export const deckEntriesKey = (uuid: string) => `${DECK_BASE_KEY}:${uuid}`;
export const deckKeyMapKey  = (uuid: string) => `${KEYMAP_STORAGE_KEY}:${uuid}`;

export const DECK_RESTORED_EVENT = 'regie-deck-restored';
export interface DeckRestoredDetail { uuid: string; deck: boolean; settings: boolean }

// Le deck enregistré n'est, à ce stade, que le remplissage par défaut : l'opérateur
// n'a encore rien décidé. Une sauvegarde serveur l'emporte alors sur lui — sans ce
// drapeau, le deck par défaut, écrit avant la réponse du serveur, la ferait ignorer.
const deckAutoKey = (uuid: string) => `mediartc_deck_auto:${uuid}`;

export function markDeckAuto(uuid: string | null, auto: boolean): void {
  if (!uuid) return;
  try {
    if (auto) localStorage.setItem(deckAutoKey(uuid), '1');
    else      localStorage.removeItem(deckAutoKey(uuid));
  } catch {}
}

export function kindOfSource(s: VideoSource): Exclude<DeckEntryKind, 'layout'> {
  return s.type === 'device' ? 'device' : s.type === 'remote' ? 'remote' : 'other';
}

// ── Réglages du deck rangés par source (effets audio, réglages vidéo) ─────────
//
// Ces clés sont déjà cloisonnées par chaîne et écrites ailleurs (LiveStreamManager,
// MediaStreamContext pour sourceAudioEffects) : leur FORMAT est reproduit ici, à
// garder identique. On les lit et on les rejoue telles quelles, sans les interpréter.
// Les effets audio n'ont plus qu'un magasin, sourceAudioEffects : les anciennes
// copies du pupitre (audioEffects, audioEffectConfigs) encore présentes dans les
// sauvegardes serveur sont ignorées à la restauration.
export const deckSettingsKeys = (uuid: string) => ({
  videoEffects      : `mediartc_regie_video_effects_settings:${uuid}`,
  sourceAudioEffects: `mediartc_source_audio_effects:${uuid}`,
  slotConfigs       : `mediartc_regie_slot_configs:${uuid}`,   // multiview (LiveStreamManager)
  audioOnlySources  : `mediartc_regie_audio_only_sources:${uuid}`,   // cf. utils_audioOnlySource.ts
  autoDirector      : `mediartc_regie_auto_director:${uuid}`,        // cf. useAutoDirector.ts
  audioSwitch       : `mediartc_regie_audio_switch:${uuid}`,         // Switch audio (mode, source fixe, mixeur)
  // Niveaux par source : les tables du poste (globales) sont recopiées ici, limitées aux sources de
  // la chaîne (LiveStreamManager), sous forme de listes pour que remapSettingsIds suive les webcams.
  stereoVolumes     : `mediartc_regie_levels_stereo:${uuid}`,        // [[sourceId, {left,right}]]
  sourceAudio       : `mediartc_regie_levels_source_audio:${uuid}`,  // [{sourceId, volume, muted, volumeLeft, volumeRight, audioDeviceId}]
  audioDeviceIds    : `mediartc_regie_levels_audio_inputs:${uuid}`,  // [[sourceId, deviceId audio]]
});

// ── Switch audio (mode Source / Fixe / Mixeur) ────────────────────────────────
//
// Écrit seulement sur action de l'opérateur (ou chargement d'un deck), JAMAIS par la
// remise à zéro de la régie : celle-ci replace le Switch sur « Source » à chaque
// activation de chaîne, et l'écrire effacerait ce qu'on veut retrouver.
export interface StoredAudioSwitch {
  mode              : 'source' | 'fixe' | 'mix';
  fixedAudioSourceId: string;
  mixGains         ?: Record<string, number>;
  mixMuted         ?: Record<string, boolean>;
}

export function loadAudioSwitch(uuid: string | null): StoredAudioSwitch | null {
  if (!uuid) return null;
  try {
    const v = JSON.parse(localStorage.getItem(deckSettingsKeys(uuid).audioSwitch) || 'null');
    if (!v || !['source', 'fixe', 'mix'].includes(v.mode)) return null;
    return { mode: v.mode, fixedAudioSourceId: typeof v.fixedAudioSourceId === 'string' ? v.fixedAudioSourceId : '',
             mixGains: v.mixGains ?? {}, mixMuted: v.mixMuted ?? {} };
  } catch { return null; }
}

export function saveAudioSwitch(uuid: string | null, s: StoredAudioSwitch | null): void {
  if (!uuid) return;
  try {
    if (s) localStorage.setItem(deckSettingsKeys(uuid).audioSwitch, JSON.stringify(s));
    else   localStorage.removeItem(deckSettingsKeys(uuid).audioSwitch);
  } catch {}
}

/** Réglages enregistrés pour cette chaîne (seulement ceux qui existent), ou null. */
export function loadDeckSettings(uuid: string | null): Record<string, any> | null {
  if (!uuid) return null;
  const out: Record<string, any> = {};
  for (const [name, key] of Object.entries(deckSettingsKeys(uuid))) {
    try {
      const raw = localStorage.getItem(key);
      if (raw != null) out[name] = JSON.parse(raw);
    } catch {}
  }
  return Object.keys(out).length ? out : null;
}

// ── Sources déjà vues par le remplissage automatique ──────────────────────────
//
// Le remplissage automatique du deck pose sur un bouton libre toute source qu'il
// n'a jamais vue. Cette mémoire ne vivait que le temps de la session : au
// rechargement de la page ou d'une sauvegarde, une source volontairement laissée
// sans bouton passait pour une nouveauté et se retrouvait affectée. On la garde
// donc par chaîne, et la sauvegarde du deck en emporte une copie.
const deckKnownKey = (uuid: string) => `mediartc_deck_known:${uuid}`;

export function loadKnownSources(uuid: string | null): string[] {
  if (!uuid) return [];
  try { const v = JSON.parse(localStorage.getItem(deckKnownKey(uuid)) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
}

export function saveKnownSources(uuid: string | null, ids: Iterable<string>): void {
  if (!uuid) return;
  try { localStorage.setItem(deckKnownKey(uuid), JSON.stringify(Array.from(new Set(ids)))); } catch {}
}

export function clearKnownSources(uuid: string | null): void {
  if (!uuid) return;
  try { localStorage.removeItem(deckKnownKey(uuid)); } catch {}
}

// ── Stockage local ────────────────────────────────────────────────────────────

/** null = rien d'enregistré pour cette chaîne. */
export function loadDeckEntries(uuid: string | null): DeckEntries | null {
  if (!uuid) return null;
  try {
    const raw = localStorage.getItem(deckEntriesKey(uuid));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch { return null; }
}

export function saveDeckEntries(uuid: string | null, entries: DeckEntries): void {
  if (!uuid) return;
  try { localStorage.setItem(deckEntriesKey(uuid), JSON.stringify(entries)); } catch {}
}

/** Raccourcis enregistrés pour cette chaîne, ou null. */
export function loadStoredKeyMap(uuid: string | null): any | null {
  if (!uuid) return null;
  try {
    const raw = localStorage.getItem(deckKeyMapKey(uuid));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

/**
 * Reprend la sauvegarde serveur UNIQUEMENT là où ce poste n'a rien : ce qui est
 * déjà enregistré ici fait foi. Retourne true si quelque chose a été repris.
 */
export async function restoreDeckFromServer(uuid: string, data: any): Promise<boolean> {
  let applied = false, deckApplied = false, settingsApplied = false;
  try {
    // Webcams locales : les identifiants de la sauvegarde sont ceux du poste qui l'a
    // écrite. On les ramène à celles de CE poste, par rang (cf. buildDeviceIdRemap),
    // pour le placement ET pour les réglages rangés par source.
    const serverEntries: DeckEntries | undefined = Array.isArray(data?.deck?.entries)
      ? JSON.parse(JSON.stringify(data.deck.entries)) : undefined;
    const { remap } = await deviceRemapFromServer(data);
    if (serverEntries) {
      serverEntries.forEach(e => {
        const target = e?.kind === 'device' ? e.ids.map(id => remap.get(id)).find(Boolean) : undefined;
        if (e && target) e.ids = [target, ...e.ids.filter(id => id !== target)].slice(0, MAX_IDS);
      });
    }

    // Réglages par source : repris UN PAR UN là où ce poste n'a rien.
    const keys = deckSettingsKeys(uuid) as Record<string, string>;
    for (const [name, value] of Object.entries(data?.deckSettings ?? {})) {
      const key = keys[name];
      if (key && value != null && localStorage.getItem(key) == null) {
        localStorage.setItem(key, JSON.stringify(remapSettingsIds(value, remap)));
        applied = settingsApplied = true;
      }
    }
    const localAuto = localStorage.getItem(deckAutoKey(uuid)) === '1';
    if (serverEntries && (localStorage.getItem(deckEntriesKey(uuid)) == null || localAuto)) {
      localStorage.setItem(deckEntriesKey(uuid), JSON.stringify(serverEntries));
      markDeckAuto(uuid, false);
      applied = deckApplied = true;
    }
    if (data?.keymap && localStorage.getItem(deckKeyMapKey(uuid)) == null) {
      localStorage.setItem(deckKeyMapKey(uuid), JSON.stringify(data.keymap));
      applied = true;
    }
  } catch {}
  if (applied) window.dispatchEvent(new CustomEvent<DeckRestoredDetail>(DECK_RESTORED_EVENT, { detail: { uuid, deck: deckApplied, settings: settingsApplied } }));
  return applied;
}

// ── Webcams locales d'un autre poste ──────────────────────────────────────────

/**
 * Correspondance « identifiant de webcam de la sauvegarde → webcam de CE poste »
 * (même règle de rang que le deck), et ensemble des webcams de ce poste. Sert aussi
 * aux emplacements PiP, qui mémorisent leur webcam par deviceId.
 */
export async function deviceRemapFromServer(data: any): Promise<{ remap: Map<string, string>; localIds: Set<string> }> {
  const localIds = await localCameraIds();
  const entries: DeckEntries | undefined = Array.isArray(data?.deck?.entries)
    ? JSON.parse(JSON.stringify(data.deck.entries)) : undefined;
  return { remap: buildDeviceIdRemap(entries, localIds), localIds: new Set(localIds) };
}

/** deviceId des caméras de CE poste, dans l'ordre d'énumération du navigateur. */
async function localCameraIds(): Promise<string[]> {
  try {
    const all = await navigator.mediaDevices.enumerateDevices();
    return all.filter(d => d.kind === 'videoinput' && !!d.deviceId).map(d => d.deviceId);
  } catch { return []; }
}

/**
 * Identifiant sauvegardé → identifiant de CE poste, pour les webcams locales.
 * Même règle que le placement du deck : une webcam déjà connue de ce poste se
 * retrouve elle-même ; les autres emplacements « webcam locale », pris par rang,
 * reçoivent chacun la première webcam locale qu'aucune entrée ne revendique.
 * Tous les identifiants d'une entrée mènent à la même cible.
 */
export function buildDeviceIdRemap(entries: DeckEntries | undefined, localIds: string[]): Map<string, string> {
  const remap = new Map<string, string>();
  if (!entries || localIds.length === 0) return remap;
  const local   = new Set(localIds);
  const devices = entries.filter((e): e is DeckEntry => e?.kind === 'device')
                         .sort((a, b) => (a.localRank ?? 0) - (b.localRank ?? 0));

  const claimed = new Set<string>();
  const pending: DeckEntry[] = [];
  for (const e of devices) {
    const mine = e.ids.find(id => local.has(id));
    if (mine) { e.ids.forEach(id => remap.set(id, mine)); claimed.add(mine); }
    else pending.push(e);
  }
  const everNamed = new Set(devices.flatMap(e => e.ids));
  const spare = localIds.filter(id => !claimed.has(id) && !everNamed.has(id));
  for (const e of pending) {
    const target = spare.shift();
    if (!target) break;
    e.ids.forEach(id => remap.set(id, target));
  }
  return remap;
}

/**
 * Retouche la webcam d'emplacements PiP venus d'ailleurs (sauvegarde, autre poste) :
 * correspondance par rang si on en a une, sinon l'identifiant est gardé s'il existe
 * ici et vidé s'il n'existe pas (jamais d'identifiant mort). Sans caméra connue,
 * rien n'est touché.
 */
export function retargetPipDevices<T extends { deviceId?: string }>(
  slots: T[], localIds: Set<string>, remap?: Map<string, string>,
): T[] {
  return slots.map(sl => {
    const id = sl.deviceId;
    if (!id) return sl;
    const target = remap?.get(id);
    if (target) return { ...sl, deviceId: target };
    return localIds.size > 0 && !localIds.has(id) ? { ...sl, deviceId: '' } : sl;
  });
}

/** Réécrit les identifiants de source d'un réglage sérialisé (paires [id, valeur] ou enregistrements { sourceId }). */
export function remapSettingsIds(value: any, remap: Map<string, string>): any {
  if (!Array.isArray(value) || remap.size === 0) return value;
  return value.map(item => {
    if (Array.isArray(item) && typeof item[0] === 'string') return [remap.get(item[0]) ?? item[0], ...item.slice(1)];
    if (item && typeof item === 'object' && typeof item.sourceId === 'string') return { ...item, sourceId: remap.get(item.sourceId) ?? item.sourceId };
    return item;
  });
}

// ── Fusion état courant ↔ intentions ──────────────────────────────────────────

/**
 * Nouvelles entrées : l'état courant l'emporte là où il y a une source ou une
 * scène ; un emplacement vide garde son intention (place réservée). Les
 * suppressions VOULUES se font en amont, en vidant `persisted[i]`.
 */
export function mergeDeckEntries(
  persisted : DeckEntries,
  slots     : Array<VideoSource | null>,
  layouts   : Array<LayoutSlot | null>,
): DeckEntries {
  const out: DeckEntries = slots.map((s, i) => {
    const l = layouts[i];
    const p = persisted[i] ?? null;
    if (l) return { kind: 'layout' as const, ids: [l.id] };
    if (s) {
      const kind = kindOfSource(s);
      const keep = p && p.kind === kind && p.ids.includes(s.id);
      const ids  = keep ? [s.id, ...p!.ids.filter(x => x !== s.id)].slice(0, MAX_IDS) : [s.id];
      return { kind, ids };
    }
    return p;
  });
  let rank = 0;
  out.forEach(e => { if (e?.kind === 'device') e.localRank = rank++; else if (e) delete e.localRank; });
  return out;
}

/**
 * Replace les sources et scènes disponibles à leur emplacement mémorisé.
 * Modifie `slots`, `layouts` et `entries` en place ; ne touche pas aux
 * emplacements déjà occupés.
 */
export function applyDeckEntries(
  entries     : DeckEntries,
  slots       : Array<VideoSource | null>,
  layouts     : Array<LayoutSlot | null>,
  sources     : VideoSource[],
  layoutSlots : LayoutSlot[],
): { slotsChanged: boolean; layoutsChanged: boolean } {
  let slotsChanged = false, layoutsChanged = false;
  const used = new Set(slots.filter((s): s is VideoSource => !!s).map(s => s.id));
  const free = (i: number) => !slots[i] && !layouts[i];

  // 1. Scènes
  entries.forEach((e, i) => {
    if (e?.kind !== 'layout' || !free(i)) return;
    const l = layoutSlots.find(x => x.id === e.ids[0]);
    if (l && !layouts.some(x => x?.id === l.id)) { layouts[i] = l; layoutsChanged = true; }
  });

  // 2. Retrouvailles exactes, tous types confondus (webcam locale comprise :
  //    retour sur le poste d'origine).
  entries.forEach((e, i) => {
    if (!e || e.kind === 'layout' || !free(i)) return;
    const src = sources.find(s => !used.has(s.id) && kindOfSource(s) === e.kind && e.ids.includes(s.id));
    if (src) { slots[i] = src; used.add(src.id); slotsChanged = true; }
  });

  // 3. Webcams locales inconnues de ce deck : la n-ième prend l'emplacement
  //    réservé à la n-ième. Une webcam déjà connue d'une entrée est exclue, même
  //    absente de ses places : elle a la sienne.
  const known = new Set<string>();
  entries.forEach(e => { if (e?.kind === 'device') e.ids.forEach(id => known.add(id)); });
  const pool = sources.filter(s => s.type === 'device' && !used.has(s.id) && !known.has(s.id));
  const pending = entries
    .map((e, i) => ({ e, i }))
    .filter(x => x.e?.kind === 'device' && free(x.i))
    .sort((a, b) => (a.e!.localRank ?? 0) - (b.e!.localRank ?? 0));
  for (const { e, i } of pending) {
    const src = pool.shift();
    if (!src) break;
    slots[i] = src; used.add(src.id); slotsChanged = true;
    e!.ids = [src.id, ...e!.ids].slice(0, MAX_IDS);   // l'ancien identifiant reste connu
  }

  return { slotsChanged, layoutsChanged };
}
